/**
 * Image src-set helper.
 *
 * The backend image preprocessor writes 2 WEBP variants alongside every
 * uploaded image: <stem>_1200.webp (main) and <stem>_600.webp (thumb).
 * When a stored image URL matches the `_1200.webp` convention, this helper
 * emits a responsive <img srcSet> so browsers download the 600 variant on
 * narrow viewports and the 1200 on desktop — without changing what we
 * persist in the DB (single URL per image).
 *
 * Any URL that DOESN'T match the convention (legacy Pexels, externally
 * pasted CDN links, etc.) is returned untouched so existing products stay
 * pixel-perfect.
 */

// Hosts whose paths we now serve through the same origin. Historic uploads
// were recorded with the internal cluster hostname; rewriting them to a
// path-relative URL means the browser hits the production storefront origin
// instead of an unreachable internal hostname. (Files behind the original
// host are 404 because K8s ephemeral disk does not persist across redeploys
// — this rewrite at least keeps the network path correct for any image
// that does still exist.)
const LEGACY_UPLOAD_HOSTS = /^https?:\/\/(?:auto-glass-(?:admin-pro|finder-1)\.[^/]+|.*\.preview\.emergentagent\.com|.*\.emergent\.host)/i;

export function normalizeImageUrl(url) {
  if (!url || typeof url !== "string") return url;
  // Strip the bad host prefix down to a relative path; the browser will
  // resolve it against the storefront origin.
  if (LEGACY_UPLOAD_HOSTS.test(url) && url.includes("/api/uploads/")) {
    return url.replace(LEGACY_UPLOAD_HOSTS, "");
  }
  return url;
}

export function imgSrcSet(url, { sizes = "100vw" } = {}) {
  if (!url || typeof url !== "string") return { src: url };
  const normalized = normalizeImageUrl(url);
  // Only rewrite our own processed assets.
  if (!normalized.endsWith("_1200.webp")) return { src: normalized };
  const small = normalized.replace(/_1200\.webp$/, "_600.webp");
  return {
    src: normalized,
    srcSet: `${small} 600w, ${normalized} 1200w`,
    sizes,
  };
}

/** Convenience: return just the thumbnail URL when one is available. */
export function thumbnailUrl(url) {
  if (!url || typeof url !== "string") return url;
  const normalized = normalizeImageUrl(url);
  if (!normalized.endsWith("_1200.webp")) return normalized;
  return normalized.replace(/_1200\.webp$/, "_600.webp");
}
